import React, { useState, useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import { appleApi } from "../../services/api";
import { FaChartLine, FaSearch } from "react-icons/fa";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
} from "chart.js";
import { Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

// Register Chart.js components
ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  CategoryScale
);

const ForecastApp = () => {
  const [appId, setAppId] = useState("");
  const [appName, setAppName] = useState("");
  const [forecastData, setForecastData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const normalizeRow = (d) => {
    const rawDate = d?.date ?? d?.ds ?? "";
    let date = "";
    if (rawDate) {
      if (typeof rawDate === "string") {
        date = rawDate.includes("T") ? rawDate.split("T")[0] : rawDate;
      } else if (rawDate instanceof Date) {
        date = rawDate.toISOString().slice(0, 10);
      }
    }

    // values could be pretty labels OR prophet keys
    const pred = Number(d?.["Predicted Installs"] ?? d?.yhat ?? 0);
    const lower = Number(d?.["Lower Bound"] ?? d?.yhat_lower ?? 0);
    const upper = Number(d?.["Upper Bound"] ?? d?.yhat_upper ?? 0);

    return { date, pred, lower, upper };
  };

  const rows = useMemo(() => {
    return Array.isArray(forecastData) ? forecastData.map(normalizeRow) : [];
  }, [forecastData]);

  const lineChartData = useMemo(
    () => ({
      labels: rows.map((r) => r.date),
      datasets: [
        {
          label: "Predicted Installs",
          data: rows.map((r) => r.pred),
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          fill: true,
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 2,
        },
        {
          label: "Lower Bound",
          data: rows.map((r) => r.lower),
          borderColor: "rgba(255, 99, 132, 1)",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          fill: true,
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 2,
        },
        {
          label: "Upper Bound",
          data: rows.map((r) => r.upper),
          borderColor: "rgba(255, 206, 86, 1)",
          backgroundColor: "rgba(255, 206, 86, 0.2)",
          fill: true,
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 2,
        },
      ],
    }),
    [rows]
  );

  const waveLineChartData = useMemo(
    () => ({
      labels: rows.map((r) => r.date),
      datasets: [
        {
          label: "Predicted Installs",
          data: rows.map((r) => r.pred),
          borderColor: "rgba(54, 162, 235, 1)",
          backgroundColor: "rgba(54, 162, 235, 0.2)",
          fill: false,
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: "rgba(54, 162, 235, 1)",
        },
        {
          label: "Lower Bound",
          data: rows.map((r) => r.lower),
          borderColor: "rgba(255, 99, 132, 1)",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          fill: false,
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: "rgba(255, 99, 132, 1)",
        },
        {
          label: "Upper Bound",
          data: rows.map((r) => r.upper),
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          fill: false,
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: "rgba(75, 192, 192, 1)",
        },
      ],
    }),
    [rows]
  );

  const barChartData = useMemo(
    () => ({
      labels: rows.map((r) => r.date),
      datasets: [
        {
          label: "Predicted Installs",
          data: rows.map((r) => r.pred),
          borderColor: "rgba(54, 162, 235, 1)",
          backgroundColor: "rgba(54, 162, 235, 0.4)",
          borderWidth: 2,
          fill: true,
        },
        {
          label: "Lower Bound",
          data: rows.map((r) => r.lower),
          borderColor: "rgba(255, 99, 71, 1)",
          backgroundColor: "rgba(255, 99, 71, 0.3)",
          borderWidth: 2,
          fill: true,
        },
        {
          label: "Upper Bound",
          data: rows.map((r) => r.upper),
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.3)",
          borderWidth: 2,
          fill: true,
        },
      ],
    }),
    [rows]
  );

  const summaryStats = useMemo(() => {
    if (!rows.length) return {};
    const installs = rows.map((r) => r.pred);
    const total = installs.reduce((acc, val) => acc + (Number(val) || 0), 0);
    return {
      average: (total / installs.length).toFixed(2),
      max: Math.max(...installs),
      min: Math.min(...installs),
    };
  }, [rows]);

  const handleSearch = async () => {
    setLoading(true);
    setError("");
    try {
      const { data: result } = await appleApi.post("forecast-installs/", {
        app_id: appId,
        app_name: appName,
      });
      setForecastData(result?.forecast ?? []);
    } catch (err) {
      setError(
        err?.result?.data?.error ||
          "Error fetching data. Please check the app ID and name."
      );
      setForecastData([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <div className="flex items-center mb-6">
          <FaChartLine className="text-blue-600 text-2xl mr-3" />
          <h2 className="text-2xl font-bold text-gray-800">App Forecasting</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App ID
            </label>
            <input
              type="text"
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              placeholder="Enter App ID"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
            />
          </div>

          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App Name
            </label>
            <input
              type="text"
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              placeholder="Enter App Name"
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
            />
          </div>

          <div className="md:col-span-2 flex items-end">
            <button
              className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-2" />
                  Loading...
                </>
              ) : (
                <>
                  <FaSearch className="mr-2" /> Search
                </>
              )}
            </button>
          </div>
        </div>

        {error && <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">{error}</div>}
      </div>

      {loading && (
        <div className="flex justify-center items-center h-64">
          <Spinner animation="border" role="status" className="text-blue-600" />
          <span className="ml-3 text-lg text-gray-600 font-medium">Loading... Please Wait</span>
        </div>
      )}

      {!loading && (
        <>
          <div className="row mb-6">
            <div className="col-md-6">
              <div
                className="bg-white rounded-xl shadow-md p-4 h-full border border-gray-100"
              >
                <h4 className="text-center text-lg font-semibold text-gray-800 mb-4">
                  Predicted Installs Over Time
                </h4>
                <Line data={lineChartData} />
              </div>
            </div>
            <div className="col-md-6">
              <div
                className="bg-white rounded-xl shadow-md p-4 h-full border border-gray-100 flex flex-col"
              >
                <h4 className="text-center text-lg font-semibold text-gray-800 mb-4">
                  <strong>Forecast Summary</strong>
                </h4>
                <div className="flex-grow">
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="font-medium text-gray-600">Average Predicted Installs:</span>
                      <span className="font-bold text-gray-900">{summaryStats.average ?? "-"}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="font-medium text-gray-600">Maximum Predicted Installs:</span>
                      <span className="font-bold text-gray-900">{summaryStats.max ?? "-"}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="font-medium text-gray-600">Minimum Predicted Installs:</span>
                      <span className="font-bold text-gray-900">{summaryStats.min ?? "-"}</span>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <h5 className="text-center font-bold text-blue-800 mb-2">
                      User Insight
                    </h5>
                    <p className="text-sm text-blue-900">
                      This forecast summary gives a clear view of your app's install
                      projections, showing not only the average expectations but also
                      the potential range between the highest and lowest predictions.
                      Use these bounds to plan marketing and resources for possible
                      fluctuations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {rows.length > 0 && (
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Predicted Installs</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lower Bound</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Upper Bound</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((r, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.pred}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.lower}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.upper}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="row d-flex align-items-stretch">
            <div className="col-md-6">
              <div
                className="bg-white rounded-xl shadow-md p-4 h-full border border-gray-100"
              >
                <h4 className="text-center text-lg font-semibold text-gray-800 mb-4">
                  Predicted Installs, Lower Bound & Upper Bound - Wave Chart
                </h4>
                <Line data={waveLineChartData} options={{ responsive: true }} />
              </div>
            </div>
            <div className="col-md-6">
              <div
                className="bg-white rounded-xl shadow-md p-4 h-full border border-gray-100"
              >
                <h4 className="text-center text-lg font-semibold text-gray-800 mb-4">
                  Bar Chart Comparison
                </h4>
                <Bar data={barChartData} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ForecastApp;

